import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Headphones, ShoppingCart, Star, Globe, ArrowRight, CheckCircle } from 'lucide-react';

const Investors = () => {
  const bookFeatures = [
    'Practical Canadian investment strategies for beginners and experienced investors',
    'Real estate investing and Canadian stock market insights',
    'Cryptocurrency and blockchain fundamentals for Canadians',
    'Tax-efficient wealth building with TFSA and RRSP optimization',
    'Retirement planning made simple for Canadian investors',
    'Humorous and engaging writing style that makes investing fun',
  ];

  const audioVersions = [
    {
      language: 'English',
      flag: '🇨🇦',
      link: 'https://music.youtube.com/playlist?list=PLa8z_InZE8ERHv0Ecg5fhPy6MZaBw_eAt',
    },
    {
      language: 'Hindi',
      flag: '🇨🇦',
      link: 'https://music.youtube.com/playlist?list=PLa8z_InZE8EQHTRXWbfjhfyM6evk92ffo',
    },
    {
      language: 'Mandarin',
      flag: '🇨🇦',
      link: 'https://music.youtube.com/playlist?list=PLa8z_InZE8EROQgN7mZsYGHSwM2yXHRDH',
    },
  ];

  // Structured data for the book
  const bookSchema = {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": "Wealthy Canadian: A Hilarious and Heartfelt Journey to Financial Freedom",
    "author": {
      "@type": "Person",
      "name": "Harry Vadalkar"
    },
    "description": "A comprehensive guide to investing in Canada covering TFSA strategies, RRSP optimization, stock market investing, real estate, cryptocurrency, and wealth building for Canadians",
    "genre": "Personal Finance",
    "inLanguage": ["en", "hi", "zh"],
    "about": ["Canadian Investing", "Personal Finance Canada", "TFSA", "RRSP", "Wealth Building"],
    "audience": {
      "@type": "Audience",
      "audienceType": "Canadian Investors"
    }
  };

  return (
    <div className="min-h-screen" data-testid="investors-page">
      <Helmet>
        <title>Wealthy Canadian Book | Investing in Canada Guide | TFSA & RRSP Strategies | Best Canadian Finance Book | Harry Vadalkar</title>
        <meta name="description" content="Wealthy Canadian - the bestselling Canadian investing book. Learn TFSA strategies, RRSP optimization, stock market investing, real estate, cryptocurrency, and wealth building. Available in English, Hindi, and Mandarin audiobook on YouTube Music. Your complete guide to financial freedom in Canada." />
        <meta name="keywords" content="Wealthy Canadian book, Wealthy Canadian audiobook, investing in Canada book, Canadian investing guide, best investing book Canada, TFSA investing strategies, TFSA tips, RRSP optimization, RRSP strategies, FHSA guide, stock market Canada, Canadian stocks for beginners, real estate investing Canada, cryptocurrency Canada, Bitcoin Canada, wealth building Canada, personal finance book Canada, Harry Vadalkar book, financial freedom Canada, Canadian investor guide, ETF investing Canada, best Canadian ETFs, mutual funds Canada, retirement planning Canada, tax-free savings account strategies, registered retirement savings plan tips, dividend investing Canada, compound interest, beginner investing Canada, financial literacy Canada, Hindi investing book, Mandarin finance book" />
        <link rel="canonical" href="https://harryvadalkar.ca/investors" />
        <meta property="og:title" content="Wealthy Canadian Book | Complete Guide to Investing in Canada" />
        <meta property="og:description" content="Bestselling Canadian investing book. Learn TFSA, RRSP strategies, stock market, real estate, and cryptocurrency investing. Build wealth the Canadian way." />
        <script type="application/ld+json">
          {JSON.stringify(bookSchema)}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/20 to-[#020617]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full mb-6">
                <Star className="w-4 h-4" />
                <span className="font-medium">Best Selling Book</span>
              </div>
              <h1 className="font-heading text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
                Wealthy <span className="text-amber-400">Canadian</span>
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                A hilarious and heartfelt journey to financial freedom, designed for every Canadian. From ramen noodles to real estate—discover the path to building lasting wealth.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://www.amazon.ca/Wealthy-Canadian-hilarious-heartfelt-journey-ebook/dp/B0FDDBRKV7"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="buy-book-amazon"
                  className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-amber-500/30"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Buy on Amazon
                </a>
              </div>
            </div>

            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl shadow-amber-500/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_e36899ae-c2fa-440f-bf36-c9469e111964/artifacts/tx8gw0to_Screen%20Shot%202026-02-09%20at%208.22.50%20AM.png"
                  alt="Wealthy Canadian Book Cover - A hilarious and heartfelt journey to wealth in Canada by Harry Vadalkar"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-slate-900 border border-amber-500/30 rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-300 text-sm">"Life-changing financial advice!"</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Book Features */}
      <section className="py-20 bg-slate-900/30" data-testid="book-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              What You'll <span className="text-amber-400">Learn</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Everything you need to build wealth the Canadian way
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bookFeatures.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 glass rounded-xl p-6 hover:border-amber-500/30 transition-all"
              >
                <CheckCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-slate-200 text-lg">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audio Books Section */}
      <section className="py-20" data-testid="audiobooks-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full mb-6">
              <Headphones className="w-4 h-4" />
              <span className="font-medium">Listen Anywhere</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Audio Book <span className="text-blue-400">Versions</span>
            </h2>
            <p className="text-slate-400 text-lg">
              Available in multiple languages on YouTube Music
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {audioVersions.map((version, index) => (
              <a
                key={index}
                href={version.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`audiobook-${version.language.toLowerCase()}`}
                className="group glass rounded-xl p-8 text-center hover:border-blue-500/50 transition-all"
              >
                <span className="text-5xl mb-4 block">{version.flag}</span>
                <h3 className="font-heading text-xl font-bold text-white mb-2">{version.language}</h3>
                <p className="text-blue-400 group-hover:text-blue-300 flex items-center justify-center gap-2">
                  Listen Now
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" data-testid="investors-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your <span className="text-amber-400">Wealth Journey</span>?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Join thousands of Canadians who have transformed their financial future with Wealthy Canadian.
          </p>
          <a
            href="https://www.amazon.ca/Wealthy-Canadian-hilarious-heartfelt-journey-ebook/dp/B0FDDBRKV7"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="investors-final-cta"
            className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-black font-bold px-10 py-4 rounded-full transition-all shadow-lg hover:shadow-amber-500/30 text-lg"
          >
            Get Your Copy Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default Investors;